import React, { forwardRef } from 'react';
import { Controller } from 'react-hook-form';

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string | React.ReactNode;
  error?: string;
  name: string;
  control?: any;
  containerClassName?: string;
  labelClassName?: string;
  checkboxClassName?: string;
  errorClassName?: string;
  variant?: 'default' | 'custom';
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      error,
      name,
      control,
      containerClassName = '',
      labelClassName = '',
      checkboxClassName = '',
      errorClassName = '',
      variant = 'default',
      className = '',
      ...props
    },
    ref
  ) => {
    const baseCheckboxStyles =
      variant === 'custom'
        ? `
        w-6 h-6 rounded border-2 border-gray-300 
        text-green-600 bg-white
        focus:ring-2 focus:ring-green-500 focus:ring-offset-0
        checked:bg-green-600 checked:border-green-600
        transition-colors duration-200
      `
        : 'figma-checkbox';

    const combinedCheckboxStyles =
      `${baseCheckboxStyles} ${checkboxClassName} ${className}`.trim();

    const renderCheckbox = (inputProps: any) => (
      <div className={`flex items-start gap-2 ${containerClassName}`}>
        <input
          ref={ref}
          id={name}
          name={name}
          type="checkbox"
          className={`${combinedCheckboxStyles} flex-shrink-0`}
          {...props}
          {...inputProps}
        />
        {label && (
          <label
            htmlFor={name}
            className={`text-sm text-[#504F4F] cursor-pointer leading-relaxed font-medium font-poppins flex-1 ${labelClassName}`}>
            {label}
          </label>
        )}
      </div>
    );

    const renderError = (message?: string) =>
      message ? (
        <p className={`text-xs text-red-600 ml-8 ${errorClassName}`}>
          {message}
        </p>
      ) : null;

    // Controlled (react-hook-form) mode
    if (control) {
      return (
        <div className="space-y-1">
          <Controller
            name={name}
            control={control}
            render={({
              field: { onChange, value, ...field },
              fieldState: { error: fieldError }
            }) => (
              <>
                {renderCheckbox({
                  ...field,
                  checked: value || false,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    onChange(e.target.checked)
                })}
                {renderError(fieldError?.message)}
              </>
            )}
          />
        </div>
      );
    }

    // Uncontrolled mode
    return (
      <div className="space-y-1">
        {renderCheckbox({})}
        {renderError(error)}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
export default Checkbox;
